import { type BranchNameType } from '@lg/shared'
import { ScreenQualifiedNameAttribute } from '@lg/shared/for-apps'
import { AppContext } from '../app-context.js'
import { computed, inject, Injectable, signal } from '@angular/core'
import { IdbService } from './idb.service.js'
import { FrontendUserBridge } from '../bridges/frontend-user-bridge.js'
import { EnvironmentSettings } from './environment-settings.js'
import { idbSettingName } from './environment-settings.js'
import { getBoEditorPath } from '@lg/shared/for-apps'
import { BoReference } from '@lg/shared/for-apps'
import { AuthService } from './auth-service.js'
import { delay, filter, map, merge, shareReplay, Subject } from 'rxjs'
import { NavigationEnd, Router } from '@angular/router'
import { toSignal } from '@angular/core/rxjs-interop'

const OverlayElementId = 'dev-bar-screen-overlay'
const NameElementId = 'dev-bar-screen-name'
const TempSessionStorageName = 'lowgile_SessionStore'

interface OverlayDivs {
	overlay: HTMLDivElement
	screenName: HTMLDivElement
}
interface DevBarSettings {
	isAutoReload: boolean
	isKeepData: boolean
	isFloating: boolean
	isTransparent: boolean
	isEnableEverything: boolean
	isIgnoreValidations: boolean
	isHomeOnSwimlaneChange: boolean
}
const DevBarSettingsIdbKey = 'Lowgile_DevBarSettings'

@Injectable({
	providedIn: 'root',
})
export class DevBarService {
	private router = inject(Router)

	environmentSettings = signal<EnvironmentSettings | undefined>(undefined)
	private settings = signal({} as DevBarSettings)
	isAutoReload = computed(() => this.settings()?.isAutoReload ?? false)
	isKeepData = computed(() => this.settings()?.isKeepData ?? false)
	isEnableEverything = computed(
		() => this.settings()?.isEnableEverything ?? false,
	)
	isIgnoreValidations = computed(
		() => this.settings()?.isIgnoreValidations ?? false,
	)
	isHomeOnSwimlaneChange = computed(
		() => this.settings()?.isHomeOnSwimlaneChange ?? false,
	)
	isFloating = computed(() => this.settings()?.isFloating ?? false)
	isTransparent = computed(() => this.settings()?.isTransparent ?? false)
	isCollapsed = signal(true)
	canUseDevBar = signal<boolean | undefined>(undefined)

	forceScreenRefreshSubject = new Subject<void>()
	refreshScreenNameSubject = new Subject<void>()
	mainScreenName$ = merge(
		this.refreshScreenNameSubject,
		this.router.events.pipe(
			filter((event) => event instanceof NavigationEnd),
			delay(10),
		),
	).pipe(
		map(() => this.findTopLevelScreenName()),
		shareReplay(1),
	)
	mainScreenName = toSignal(this.mainScreenName$)

	constructor(
		private idbService: IdbService,
		private authService: AuthService,
	) {}

	async init() {
		await this.authService.isInitializedPromise.promise
		const user = FrontendUserBridge.currentUser

		this.environmentSettings.set(await this.idbService.get(idbSettingName))
		const settings = (await this.idbService.get(DevBarSettingsIdbKey)) ?? {
			isAutoReload: false,
			isKeepData: false,
			isFloating: false,
			isTransparent: false,
			isEnableEverything: false,
			isIgnoreValidations: false,
			isHomeOnSwimlaneChange: false,
		}
		this.settings.set(settings)
		this.isCollapsed.set(!settings.isFloating)
		this.canUseDevBar.set(user?.hasSystemPermission('UseConsole') ?? false)
	}

	setIsAutoReload(isAutoReload: boolean) {
		this.settings.update((settings) => ({ ...settings, isAutoReload }))
		this.saveSettings()
	}

	setIsKeepData(isKeepData: boolean) {
		this.settings.update((settings) => ({ ...settings, isKeepData }))
		this.saveSettings()
	}

	setIsFloating(isFloating: boolean) {
		this.settings.update((settings) => ({ ...settings, isFloating }))
		this.saveSettings()
	}

	setIsTransparent(isTransparent: boolean) {
		this.settings.update((settings) => ({ ...settings, isTransparent }))
		this.saveSettings()
	}

	setIsEnableEverything(isEnableEverything: boolean) {
		this.settings.update((settings) => ({ ...settings, isEnableEverything }))
		this.saveSettings()
	}

	setIsIgnoreValidations(isIgnoreValidations: boolean) {
		this.settings.update((settings) => ({ ...settings, isIgnoreValidations }))
		this.saveSettings()
	}

	setIsHomeOnSwimlaneChange(isHomeOnSwimlaneChange: boolean) {
		this.settings.update((settings) => ({
			...settings,
			isHomeOnSwimlaneChange,
		}))
		this.saveSettings()
	}

	private async saveSettings() {
		await this.idbService.put(DevBarSettingsIdbKey, this.settings())
		this.settings.update((settings) => settings)
		// this.forceScreenRefreshSubject.next()
	}

	openBoEditor(branchName: BranchNameType, boRef: BoReference) {
		const path = getBoEditorPath(branchName, boRef)

		window.open(path, path)
	}

	handleMouseEvent(event: MouseEvent) {
		const highlightModeActive = event.altKey && event.ctrlKey
		if (!highlightModeActive) {
			this.removeOverlayDivs()
			return
		}

		const overlayDivs = this.getOrCreateOverlayDivs()

		switch (event.type) {
			case 'click':
				return this.openDefiningScreenAtMouseEvent(event)

			case 'mousemove':
				return this.updateOverlay(event, overlayDivs)
		}
	}

	private updateOverlay(event: MouseEvent, overlayDivs: OverlayDivs) {
		const definingScreen = this.findDefiningScreenAtPosition(event.x, event.y)
		if (definingScreen.qualifiedName && definingScreen.rect) {
			const style = overlayDivs.overlay.style
			const { rect } = definingScreen
			style.top = `${rect.top}px`
			style.left = `${rect.left}px`
			style.width = `${rect.width}px`
			style.height = `${rect.height}px`

			overlayDivs.screenName.innerText = definingScreen.qualifiedName
		}
	}

	private findDefiningScreenAtPosition(
		x: number,
		y: number,
	): {
		qualifiedName?: string
		rect?: DOMRect
	} {
		let foundScreenId: string | undefined = undefined
		let foundRect: DOMRect | undefined = undefined

		for (const el of document.elementsFromPoint(x, y)) {
			const screenId = el.getAttribute(ScreenQualifiedNameAttribute)
			if (screenId) {
				if (foundScreenId && foundScreenId != screenId) {
					break
				}
				foundScreenId = screenId
				foundRect = el.getBoundingClientRect()
			}
		}

		return {
			qualifiedName: foundScreenId,
			rect: foundRect,
		}
	}

	private openDefiningScreenAtMouseEvent(event: MouseEvent) {
		if (event.target instanceof HTMLElement) {
			const { qualifiedName } = this.findDefiningScreenAtPosition(
				event.x,
				event.y,
			)
			if (qualifiedName) {
				const boRef = BoReference.fromQualifiedName(qualifiedName, 'Screen')
				this.openBoEditor(AppContext.branchName, boRef)
			}
		}
	}

	findTopLevelScreenName(): string {
		return (
			document
				.querySelector(`[${ScreenQualifiedNameAttribute}]`)
				?.attributes?.getNamedItem(ScreenQualifiedNameAttribute)?.value ?? ''
		)
	}

	getTopLevelProcessName(): string {
		return AppContext.processId()
	}

	getOrCreateOverlayDivs(): OverlayDivs {
		let overlay = document.getElementById(OverlayElementId)
		if (!overlay) {
			overlay = document.createElement('div')
			overlay.id = OverlayElementId
			overlay.innerHTML = `<div id="${NameElementId}"></div>`
			document.body.appendChild(overlay)
		}

		return {
			overlay: overlay as HTMLDivElement,
			screenName: document.getElementById(NameElementId) as HTMLDivElement,
		}
	}

	removeOverlayDivs() {
		const overlay = document.getElementById(OverlayElementId)
		if (overlay) document.body.removeChild(overlay)
	}

	saveToSessionStorage() {
		// const entries = Object.entries(this.componentStores).map(([key, store]) => [key, store.stateInternal])
		// const stateObj = Object.fromEntries(entries)
		// TODO: reimplement
		// const stateObj = { app: this.componentStores.app.stateInternal }
		// console.log('storing state', stateObj)
		// const stateAndDuplicatesJson = AppContext.jsonMapper.writeToJsonRemovingDuplicates(stateObj)
		// sessionStorage.setItem(TempSessionStorageName, JSON.stringify(stateAndDuplicatesJson))
	}

	loadFromSessionStorage() {
		// TODO: reimplement
		// const stateAndDuplicatesJson = sessionStorage.getItem(TempSessionStorageName)
		// if(!stateAndDuplicatesJson) return
		// const stateAndDuplicates = JSON.parse(stateAndDuplicatesJson)

		// let stateObj = AppContext.jsonMapper.readFromValueAndDuplicatePaths(stateAndDuplicates)
		// const entries = Object.entries(stateObj).map(([key, state]) => [key, new Store(key, state as object)])
		// this.componentStores = Object.fromEntries(entries)
		this.clearSessionStorage()
	}

	clearSessionStorage() {
		sessionStorage.removeItem(TempSessionStorageName)
	}
}
